/**
 * Shared foundation for the Payment Calendar v2 "cockpit".
 *
 * One place for the row/day types (derived from the generated
 * `PaymentCalendarQuery`), the tone/status/income-state vocabulary, the
 * date-range + week math, money formatting, the derived-row → override input
 * builder (generalised from `PlannedPaymentModal`), the reschedule / mark-paid
 * action hooks, and the small presentational atoms (tone dot, chips) every
 * visual part composes on top of.
 *
 * Visual parts import EVERYTHING they need from here so they all share one
 * `TCalendarRow` / `TCalendarDay` type and one set of helpers.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { formatMoneyCurrency } from 'core/utils/format';
import { useToasts } from 'core/hooks';
import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  PaymentCalendarQuery,
  PlannedPaymentInput,
  useCreatePlannedPaymentMutation,
  useUpdatePlannedPaymentMutation,
  useUpdateExpenseMutation,
  useSetExpenseStatusMutation,
} from 'core';

// moment is only used for the ISO-week math; keep the import local to this file
// so parts can rely on `weekDates`/`rangeForView` instead of re-deriving weeks.
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment';

/* ------------------------------------------------------------------ *
 * Types — single source of truth, derived from the generated query.  *
 * ------------------------------------------------------------------ */

/** The whole `paymentCalendar` result (non-null). */
export type TCalendar = NonNullable<PaymentCalendarQuery['paymentCalendar']>;

/** One day bucket with its income/expense/received rows + per-day totals. */
export type TCalendarDay = NonNullable<NonNullable<TCalendar['days']>[number]>;

/** One payment row (income, expense or received). */
export type TCalendarRow = NonNullable<
  NonNullable<TCalendarDay['incomeRows']>[number]
>;

/* ------------------------------------------------------------------ *
 * Constants — tone / status / income-state vocabulary.               *
 * ------------------------------------------------------------------ */

export type TTone = 'ok' | 'danger' | 'muted';

/** Canonical palette for the whole cockpit. */
export const TONE = {
  ok: '#16a34a', // income / positive balance
  danger: '#ef4444', // expense / overdue / shortfall
  muted: '#94a3b8', // neutral / awaited / no-tone
} as const;

/** Income lifecycle states surfaced by the backend `incomeState` field. */
export const INCOME_STATE = {
  received: 'received',
  awaited: 'awaited',
  overdue: 'overdue',
  expected: 'expected',
} as const;
export type TIncomeState = typeof INCOME_STATE[keyof typeof INCOME_STATE];

/** PlannedPayment `status` values (mirror of the modal's STATUSES). */
export const STATUS = {
  planned: 'planned',
  confirmed: 'confirmed',
  paid: 'paid',
  cancelled: 'cancelled',
} as const;
export type TStatus = typeof STATUS[keyof typeof STATUS];

/** Calendar views the toolbar can switch between. */
export type TCalendarView =
  | 'today'
  | 'thisWeek'
  | 'nextWeek'
  | 'sixWeeks'
  | 'month';

/**
 * The right-panel scope: either a single day or a whole ISO week.
 *  - mode 'day'  → `date` is the ISO day ('YYYY-MM-DD').
 *  - mode 'week' → `date` is the ISO **Monday** of the week ('YYYY-MM-DD').
 */
export interface IScope {
  mode: 'day' | 'week';
  date: string;
}

const DATE_FMT = 'YYYY-MM-DD';

/** Sources that are stored PlannedPayments (have a real `plannedPaymentId`). */
const STORED_SOURCES = new Set(['manual', 'recurring']);

/* ------------------------------------------------------------------ *
 * Helpers — tone, week math, ranges, money, day totals.              *
 * ------------------------------------------------------------------ */

/**
 * Resolve a tone token / backend tone string / numeric balance into a hex
 * colour. Accepts:
 *  - a `TTone` ('ok' | 'danger' | 'muted')
 *  - the backend's `'green' | 'red'` tone strings
 *  - a numeric balance (>= 0 → ok, < 0 → danger)
 *  - nullish → muted
 */
export const toneColor = (
  tone: TTone | string | number | null | undefined,
): string => {
  if (typeof tone === 'number') return tone < 0 ? TONE.danger : TONE.ok;
  switch (tone) {
    case 'ok':
    case 'green':
      return TONE.ok;
    case 'danger':
    case 'red':
      return TONE.danger;
    default:
      return TONE.muted;
  }
};

/**
 * The 7 ISO-week dates (Mon..Sun) as 'YYYY-MM-DD' for the week containing
 * `anchor` (a 'YYYY-MM-DD' string or moment-parseable value; defaults to today).
 */
export const weekDates = (anchor?: string | null): string[] => {
  const start = moment(anchor || undefined).startOf('isoWeek');
  return Array.from({ length: 7 }, (_, i) =>
    start.clone().add(i, 'day').format(DATE_FMT),
  );
};

/**
 * `{ from, to }` (inclusive, 'YYYY-MM-DD') for a cockpit view, anchored at
 * `anchor` (defaults to today). Every view derives from the passed anchor so
 * the page can page weeks/months forward and back.
 *
 *  - today    → the anchor day only.
 *  - thisWeek → the ISO week containing the anchor (Mon..Sun).
 *  - nextWeek → the ISO week after the anchor's week.
 *  - sixWeeks → 6 ISO weeks (42 days) from the anchor's ISO Monday.
 *  - month    → the full ISO weeks intersecting the anchor's calendar month
 *               (the grid renders the same span).
 */
export const rangeForView = (
  view: TCalendarView,
  anchor?: string | null,
): { from: string; to: string } => {
  const base = moment(anchor || undefined);
  switch (view) {
    case 'today':
      return { from: base.format(DATE_FMT), to: base.format(DATE_FMT) };
    case 'thisWeek':
      return {
        from: base.clone().startOf('isoWeek').format(DATE_FMT),
        to: base.clone().endOf('isoWeek').format(DATE_FMT),
      };
    case 'nextWeek': {
      const next = base.clone().add(1, 'week');
      return {
        from: next.clone().startOf('isoWeek').format(DATE_FMT),
        to: next.clone().endOf('isoWeek').format(DATE_FMT),
      };
    }
    case 'sixWeeks': {
      const start = base.clone().startOf('isoWeek');
      return {
        from: start.format(DATE_FMT),
        to: start.clone().add(41, 'day').format(DATE_FMT),
      };
    }
    case 'month':
    default: {
      // Full ISO weeks intersecting the anchor's calendar month.
      const start = base.clone().startOf('month').startOf('isoWeek');
      const end = base.clone().endOf('month').endOf('isoWeek');
      return { from: start.format(DATE_FMT), to: end.format(DATE_FMT) };
    }
  }
};

/**
 * The list of weeks (each a 7-element Mon..Sun array of ISO dates) the grid
 * should render for `view`, anchored at `anchor` (defaults to today).
 *
 *  - today    → [] (no grid; the day list is the master).
 *  - thisWeek → [the anchor's week].
 *  - nextWeek → [the anchor+1 week].
 *  - sixWeeks → 6 weeks from the anchor's ISO Monday.
 *  - month    → every full ISO week intersecting the anchor's calendar month
 *               (5–6 weeks).
 */
export const weeksForView = (
  view: TCalendarView,
  anchor?: string | null,
): string[][] => {
  const base = moment(anchor || undefined);
  switch (view) {
    case 'today':
      return [];
    case 'thisWeek':
      return [weekDates(base.format(DATE_FMT))];
    case 'nextWeek':
      return [weekDates(base.clone().add(1, 'week').format(DATE_FMT))];
    case 'sixWeeks': {
      const start = base.clone().startOf('isoWeek');
      return Array.from({ length: 6 }, (_, i) =>
        weekDates(start.clone().add(i, 'week').format(DATE_FMT)),
      );
    }
    case 'month':
    default: {
      const start = base.clone().startOf('month').startOf('isoWeek');
      const end = base.clone().endOf('month').endOf('isoWeek');
      const weeks: string[][] = [];
      const cursor = start.clone();
      while (cursor.isSameOrBefore(end, 'day')) {
        weeks.push(weekDates(cursor.format(DATE_FMT)));
        cursor.add(1, 'week');
      }
      return weeks;
    }
  }
};

/** The 0-based calendar month of `anchor` — lets the grid dim out-of-month days. */
export const monthAnchorMonth = (anchor?: string | null): number =>
  moment(anchor || undefined).month();

/**
 * A human-readable label for the active period, localized through the global
 * moment locale (ru/pl). Examples: '23–29 июня 2026', 'июнь 2026',
 * '01.06–12.07.2026' (sixWeeks).
 */
export const periodLabel = (
  view: TCalendarView,
  range: { from: string; to: string },
  // `t` is accepted for API symmetry / future templating; unused for now.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  t?: (key: string) => string,
): string => {
  const from = moment(range.from);
  const to = moment(range.to);
  if (view === 'today') return from.format('D MMMM YYYY');
  if (view === 'month') return from.format('MMMM YYYY');
  if (view === 'thisWeek' || view === 'nextWeek') {
    // Same month/year → '23–29 июня 2026'; spanning a month boundary →
    // '30 июня – 6 июля 2026'.
    if (from.isSame(to, 'month')) {
      return `${from.format('D')}–${to.format('D MMMM YYYY')}`;
    }
    return `${from.format('D MMMM')} – ${to.format('D MMMM YYYY')}`;
  }
  // sixWeeks → compact numeric span.
  return `${from.format('DD.MM')}–${to.format('DD.MM.YYYY')}`;
};

/** Money formatter wrapping `formatMoneyCurrency` (defaults to PLN). */
export const fmtMoney = (
  n: number | null | undefined,
  currency: string | null | undefined = 'PLN',
): string => formatMoneyCurrency(n, currency);

/** Convenience accessor for a day's headline totals. */
export const dayTotals = (
  day: TCalendarDay | null | undefined,
): {
  income: number;
  expense: number;
  received: number;
  opening: number;
  end: number;
  tone: string | null | undefined;
} => ({
  income: day?.incomeTotalPln || 0,
  expense: day?.expenseTotalPln || 0,
  received: day?.receivedTotalPln || 0,
  opening: day?.openingBalancePln || 0,
  end: day?.endBalancePln || 0,
  tone: day?.tone,
});

/* ------------------------------------------------------------------ *
 * Override input builder — derived row → *_override PlannedPayment.   *
 * ------------------------------------------------------------------ */

/** Patch applied when converting a derived row into a stored override. */
export interface IOverridePatch {
  date?: string;
  status?: string;
  amount?: number | null;
  comment?: string | null;
}

/* ------------------------------------------------------------------ *
 * Expense source_ref parsing.                                        *
 *                                                                    *
 * The unified `expenses.Expense` surfaces into the calendar as two   *
 * kinds of rows, distinguished by `source_ref`:                      *
 *  - ONE-OFF expense       → `expense:{pk}`                          *
 *  - RECURRING occurrence  → `expense_occurrence:{pk}#{YYYY-MM-DD}`   *
 *    (the `#`-suffix is the occurrence's ORIGINAL date — the series   *
 *     anchor we override against, NOT the rescheduled date).          *
 * ------------------------------------------------------------------ */

/** A parsed expense `source_ref`. */
export interface IExpenseRef {
  /** Whether this row is a stored one-off expense or a generated occurrence. */
  kind: 'expense' | 'expense_occurrence';
  /** The owning `Expense` primary key. */
  pk: string;
  /** Recurring occurrences only: the occurrence's ORIGINAL ISO date. */
  originalDate?: string;
}

/**
 * Parse an expense `source_ref` (`expense:{pk}` or
 * `expense_occurrence:{pk}#{YYYY-MM-DD}`) into `{ kind, pk, originalDate }`.
 * Returns `null` for any non-expense ref (income / invoice / teacher_payout /
 * contribution), so callers can branch expense routing before falling through
 * to the existing income/override paths.
 */
export const parseExpenseRef = (
  sourceRef: string | null | undefined,
): IExpenseRef | null => {
  const raw = sourceRef || '';
  const [base, originalDate] = raw.split('#');
  const [prefix, ...rest] = base.split(':');
  const pk = rest.join(':'); // pks are numeric, but be defensive.
  if (prefix === 'expense' && pk) {
    return { kind: 'expense', pk };
  }
  if (prefix === 'expense_occurrence' && pk) {
    return { kind: 'expense_occurrence', pk, originalDate };
  }
  return null;
};

/**
 * Build the `createPlannedPayment` input that overrides ONE occurrence of a
 * recurring expense series (reschedule one date / mark one occurrence paid)
 * WITHOUT mutating the series. Mirrors the income/teacher-payout override
 * pattern, keyed by `(relatedExpenseId, periodDate)`.
 *
 *  - `periodDate` = the occurrence's ORIGINAL date (the series anchor).
 *  - `date`       = the (possibly rescheduled) target date.
 */
export const buildExpenseOccurrenceOverrideInput = (
  row: TCalendarRow,
  ref: IExpenseRef,
  patch: IOverridePatch = {},
): PlannedPaymentInput => {
  const originalDate =
    ref.originalDate || row.date || moment().format(DATE_FMT);
  return {
    direction: 'expense',
    kind: 'expense_occurrence_override',
    relatedExpenseId: ref.pk,
    periodDate: originalDate,
    date: patch.date ?? originalDate,
    amount: patch.amount ?? row.amount ?? 0,
    currency: row.currency || 'PLN',
    comment: patch.comment ?? row.comment ?? undefined,
    status: patch.status ?? row.status ?? STATUS.planned,
    recurrence: 'none',
  };
};

/** Parse a derived row's `sourceRef` into the override link fields. */
const overrideLinkFromSourceRef = (
  sourceRef: string | null | undefined,
): Partial<PlannedPaymentInput> & { kind: string } => {
  const base = (sourceRef || '').split('#')[0];
  const [kind, ...rest] = base.split(':');
  if (kind === 'contribution') {
    return { kind: 'income_override', relatedContributionId: rest[0] };
  }
  if (kind === 'invoice') {
    return { kind: 'income_override', relatedFakturowniaInvoiceId: rest[0] };
  }
  if (kind === 'teacher_payout') {
    const [appId, period] = rest; // APPID, 'YYYY-MM'
    const [year, month] = (period || '').split('-');
    return {
      kind: 'teacher_payout_override',
      relatedApplicationId: appId,
      periodYear: Number(year) || undefined,
      periodMonth: Number(month) || undefined,
    };
  }
  return { kind: 'income_override' };
};

/**
 * Build the create-input that materialises a DERIVED row
 * (teacher_payout / contribution / invoice) into a stored `*_override`
 * PlannedPayment, applying `patch` (date / status / amount / comment).
 *
 * Generalised from `PlannedPaymentModal`'s override path so the reschedule /
 * mark-paid actions and the modal can share one code path.
 */
export const buildOverrideInput = (
  row: TCalendarRow,
  patch: IOverridePatch = {},
): PlannedPaymentInput => {
  // Recurring-expense occurrences override via their own (expense, periodDate)
  // key — route them through the dedicated builder.
  const expenseRef = parseExpenseRef(row.sourceRef);
  if (expenseRef?.kind === 'expense_occurrence') {
    return buildExpenseOccurrenceOverrideInput(row, expenseRef, patch);
  }
  const link = overrideLinkFromSourceRef(row.sourceRef);
  return {
    direction: row.direction || 'expense',
    date: patch.date ?? row.date ?? moment().format(DATE_FMT),
    amount: patch.amount ?? row.amount ?? 0,
    currency: row.currency || 'PLN',
    comment: patch.comment ?? row.comment ?? undefined,
    status: patch.status ?? row.status ?? STATUS.planned,
    recurrence: 'none',
    ...link,
  };
};

/** Is this row a stored PlannedPayment (vs a derived/projected one)? */
export const isStoredRow = (row: TCalendarRow): boolean =>
  STORED_SOURCES.has(row.source || '') && !!row.plannedPaymentId;

/* ------------------------------------------------------------------ *
 * Drag-and-drop vocabulary (agenda «Перенести» gates + planning DnD). *
 * ------------------------------------------------------------------ */

/** DataTransfer MIME key carrying the dragged row's `sourceRef`. */
export const DRAG_KEY = 'application/x-pc-row-sourceref';

/**
 * Can this row be moved to another date? Requires an editable row with a
 * stable `sourceRef`; already-settled rows (received income / paid expense)
 * are frozen — there is nothing left to plan.
 */
export const isDraggableRow = (row: TCalendarRow): boolean =>
  !!row.editable &&
  !!row.sourceRef &&
  row.incomeState !== INCOME_STATE.received &&
  row.status !== STATUS.paid;

/**
 * Can this row be deleted from the calendar? Stored PlannedPayments (manual /
 * recurring income, materialised overrides) and ONE-OFF expenses
 * (`expense:{pk}` → `deleteExpense`) are deletable. Recurring-expense
 * OCCURRENCES are not (they are series projections — edit the series instead).
 */
export const isDeletableRow = (row: TCalendarRow): boolean =>
  isStoredRow(row) || parseExpenseRef(row.sourceRef)?.kind === 'expense';

/* ------------------------------------------------------------------ *
 * Action hooks — reschedule + mark paid, toast-wired.                *
 * ------------------------------------------------------------------ */

interface IActionHookArg {
  /** Refetch the calendar query after a successful mutation. */
  onDone?: () => void;
}

/**
 * Move a row to a new date.
 *  - stored row → `updatePlannedPayment(date)`
 *  - derived row → `createPlannedPayment(buildOverrideInput(row, { date }))`
 * Errors surface through the global toast.
 */
export const useReschedule = (
  arg: IActionHookArg = {},
): {
  reschedule: (row: TCalendarRow, newDateISO: string) => Promise<void>;
  loading: boolean;
} => {
  const { onDone } = arg;
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [
    createMutation,
    { loading: creating },
  ] = useCreatePlannedPaymentMutation();
  const [
    updateMutation,
    { loading: updating },
  ] = useUpdatePlannedPaymentMutation();
  const [
    updateExpenseMutation,
    { loading: updatingExpense },
  ] = useUpdateExpenseMutation();

  const reschedule = useCallback(
    async (row: TCalendarRow, newDateISO: string) => {
      try {
        const expenseRef = parseExpenseRef(row.sourceRef);
        if (expenseRef?.kind === 'expense') {
          // One-off expense → move its PAYMENT date (`paidAt`); the calendar
          // buckets expenses on paid_at-or-date, so this is what relocates the
          // row. The document `date` is left untouched.
          const res = await updateExpenseMutation({
            variables: { input: { id: expenseRef.pk, paidAt: newDateISO } },
          });
          const payload = res.data?.updateExpense;
          if (!payload?.success) throw new Error(payload?.errors?.join('\n'));
        } else if (expenseRef?.kind === 'expense_occurrence') {
          // Recurring occurrence → override ONE occurrence, keep the series.
          const res = await createMutation({
            variables: {
              input: buildExpenseOccurrenceOverrideInput(row, expenseRef, {
                date: newDateISO,
              }),
            },
          });
          const payload = res.data?.createPlannedPayment;
          if (!payload?.success) throw new Error(payload?.errors?.join('\n'));
        } else if (isStoredRow(row) && row.plannedPaymentId) {
          const res = await updateMutation({
            variables: {
              input: { id: String(row.plannedPaymentId), date: newDateISO },
            },
          });
          const payload = res.data?.updatePlannedPayment;
          if (!payload?.success) throw new Error(payload?.errors?.join('\n'));
        } else {
          const res = await createMutation({
            variables: {
              input: buildOverrideInput(row, { date: newDateISO }),
            },
          });
          const payload = res.data?.createPlannedPayment;
          if (!payload?.success) throw new Error(payload?.errors?.join('\n'));
        }
        addToast({
          data: {
            type: ToastTypes.success,
            title: t('pages.paymentCalendar.reschedule.moved'),
          },
          type: GlobalToastTypeNames.PaymentCalendar,
        });
        onDone?.();
      } catch (error) {
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.paymentCalendar.toasts.saveFailed'),
            text: (error as Error).message,
          },
          type: GlobalToastTypeNames.PaymentCalendar,
        });
      }
    },
    [
      updateMutation,
      createMutation,
      updateExpenseMutation,
      addToast,
      onDone,
      t,
    ],
  );

  return {
    reschedule,
    loading: creating || updating || updatingExpense,
  };
};

/**
 * Set a row's payment status to any of planned/confirmed/paid/cancelled — the
 * inline status control (chip-menu) and the «Оплатить» quick button both call
 * this. Routing mirrors `useReschedule`:
 *  - one-off expense        → `setExpenseStatus(id, status)` (derives paid_at)
 *  - recurring occurrence    → `createPlannedPayment(override, { status })`
 *  - stored PlannedPayment   → `updatePlannedPayment(status)`
 *  - other derived row       → `createPlannedPayment(buildOverrideInput, { status })`
 * Errors surface through the global toast.
 */
export const useSetRowStatus = (
  arg: IActionHookArg = {},
): {
  setStatus: (row: TCalendarRow, status: string) => Promise<void>;
  loading: boolean;
} => {
  const { onDone } = arg;
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [
    createMutation,
    { loading: creating },
  ] = useCreatePlannedPaymentMutation();
  const [
    updateMutation,
    { loading: updating },
  ] = useUpdatePlannedPaymentMutation();
  const [
    setExpenseStatusMutation,
    { loading: settingExpense },
  ] = useSetExpenseStatusMutation();

  const setStatus = useCallback(
    async (row: TCalendarRow, status: string) => {
      try {
        const expenseRef = parseExpenseRef(row.sourceRef);
        if (expenseRef?.kind === 'expense') {
          // One-off expense → flip the Expense itself; the backend derives
          // paid_at from the status (stamps today on paid, clears otherwise).
          const res = await setExpenseStatusMutation({
            variables: { id: expenseRef.pk, status },
          });
          const payload = res.data?.setExpenseStatus;
          if (!payload?.success) throw new Error(payload?.errors?.join('\n'));
        } else if (expenseRef?.kind === 'expense_occurrence') {
          // Recurring occurrence → override ONE occurrence on its original
          // date, leaving the series untouched.
          const res = await createMutation({
            variables: {
              input: buildExpenseOccurrenceOverrideInput(row, expenseRef, {
                status,
              }),
            },
          });
          const payload = res.data?.createPlannedPayment;
          if (!payload?.success) throw new Error(payload?.errors?.join('\n'));
        } else if (isStoredRow(row) && row.plannedPaymentId) {
          const res = await updateMutation({
            variables: {
              input: { id: String(row.plannedPaymentId), status },
            },
          });
          const payload = res.data?.updatePlannedPayment;
          if (!payload?.success) throw new Error(payload?.errors?.join('\n'));
        } else {
          const res = await createMutation({
            variables: {
              input: buildOverrideInput(row, { status }),
            },
          });
          const payload = res.data?.createPlannedPayment;
          if (!payload?.success) throw new Error(payload?.errors?.join('\n'));
        }
        addToast({
          data: {
            type: ToastTypes.success,
            title: t('pages.paymentCalendar.statusChange.done'),
          },
          type: GlobalToastTypeNames.PaymentCalendar,
        });
        onDone?.();
      } catch (error) {
        addToast({
          data: {
            type: ToastTypes.error,
            title: t('pages.paymentCalendar.statusChange.failed'),
            text: (error as Error).message,
          },
          type: GlobalToastTypeNames.PaymentCalendar,
        });
      }
    },
    [
      updateMutation,
      createMutation,
      setExpenseStatusMutation,
      addToast,
      onDone,
      t,
    ],
  );

  return {
    setStatus,
    loading: creating || updating || settingExpense,
  };
};

/** Back-compat thin wrapper: "mark paid" = set status to 'paid'. */
export const useMarkPaid = (
  arg: IActionHookArg = {},
): {
  markPaid: (row: TCalendarRow) => Promise<void>;
  loading: boolean;
} => {
  const { setStatus, loading } = useSetRowStatus(arg);
  const markPaid = useCallback(
    (row: TCalendarRow) => setStatus(row, STATUS.paid),
    [setStatus],
  );
  return { markPaid, loading };
};

/* ------------------------------------------------------------------ *
 * Presentational atoms — shared across all visual parts.             *
 * ------------------------------------------------------------------ */

/** A small coloured dot used to flag a row/day tone. */
export const ToneDot = styled.span<{ tone?: TTone | string | number | null }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${({ tone }) => toneColor(tone)};
`;

const baseChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
  white-space: nowrap;
`;

/** Tone for each PlannedPayment status (drives the StatusChip colour). */
export const statusTone = (status: string | null | undefined): TTone => {
  switch (status) {
    case STATUS.paid:
    case STATUS.confirmed:
      return 'ok';
    case STATUS.cancelled:
      return 'danger';
    default:
      return 'muted';
  }
};

/** Tone for each income state (drives the IncomeStateChip colour). */
export const incomeStateTone = (state: string | null | undefined): TTone => {
  switch (state) {
    case INCOME_STATE.received:
      return 'ok';
    case INCOME_STATE.overdue:
      return 'danger';
    default:
      return 'muted';
  }
};

/** A pill rendering a payment status (colour by `statusTone`). */
export const StatusChip = styled(baseChip)<{ status?: string | null }>`
  color: #fff;
  background: ${({ status }) => toneColor(statusTone(status))};
`;

/** A pill rendering an income state (colour by `incomeStateTone`). */
export const IncomeStateChip = styled(baseChip)<{ state?: string | null }>`
  color: ${({ state }) => toneColor(incomeStateTone(state))};
  background: ${({ state }) => `${toneColor(incomeStateTone(state))}1a`};
`;

/**
 * The clickable / draggable building block for a single payment row, used by
 * the day column, week strip and to-pay list. Styling only — parts wire the
 * label/amount/`onClick`/drag handlers. Tone follows the row's income state
 * (income rows) or balance-after tone (expense rows).
 */
export const PaymentChip = styled.div<{
  tone?: TTone | string | number | null;
  draggable?: boolean;
  onClick?: () => void;
}>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid #ededed;
  border-left: 3px solid ${({ tone }) => toneColor(tone)};
  background: #fff;
  font-size: 12px;
  cursor: ${({ draggable, onClick }) =>
    draggable ? 'grab' : onClick ? 'pointer' : 'default'};

  &:hover {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
`;
